import { create } from "zustand"
import { CollectionEventCreator, CollectionEventErrorValues } from "../models/CollectionEventCreator"
import { FlightlineEvent } from "../models/FlightlineEvent"
import { FlightlineStatusOption } from "../store/types/MissionsTypes"
import { StringUtils } from "../utils/StringUtils"
import { TimeUtils } from "../utils/TimeUtils"

export const initialCollectionEventCreation: CollectionEventCreator = {
    line_original_name: "",
    coll_event_name: "",
    status: FlightlineStatusOption.NORMAL,
    start_time: "",
    stop_time: "",
    duration: "",
    flown_agl: "",
    notes: "",
    fe_planned_line_ids: [],
}

interface CollectionEventEditorState {
    isDrawerOpen: boolean
    values: CollectionEventCreator
    setValuesFromFlightline: (flightline?: FlightlineEvent) => void
    setCloseEventCreator: () => void
    updateValues: (newValues: CollectionEventCreator) => void
    errors: CollectionEventErrorValues
    setErrors: (errors: CollectionEventErrorValues) => void
    crossesMidnight: boolean
    setCrossesMidnight: (bool: boolean) => void
    formIsValid: () => boolean
}

export const defaultErrorValues: CollectionEventErrorValues = {
    status: "",
    start_time: "",
    stop_time: "",
    duration: "",
    fe_planned_line_ids: "",
}

const initialEventEditorState = {
    isDrawerOpen: false,
    values: initialCollectionEventCreation,
    errors: defaultErrorValues,
    crossesMidnight: false,
}

export const useCollectionEventEditorStore = create<CollectionEventEditorState>((set, get) => ({
    ...initialEventEditorState,
    setCloseEventCreator: () => {
        set({ isDrawerOpen: false, values: initialCollectionEventCreation })
    },
    setValuesFromFlightline: (flightline) => {
        let lineInfo: CollectionEventCreator = { ...initialCollectionEventCreation }
        let crossesMidnight = false
        if (flightline) {
            lineInfo = {
                id: flightline.fe_event_id ?? undefined,
                distance: StringUtils.stringOrEmpty(flightline.flight_line_distance),
                line_original_name: StringUtils.stringOrEmpty(flightline.line_original_name),
                coll_event_name: StringUtils.stringOrEmpty(flightline.coll_event_name),
                status: StringUtils.stringOrEmpty(flightline.flight_status),
                start_time: TimeUtils.formatTime(flightline.flight_line_start_time) ?? "",
                stop_time: TimeUtils.formatTime(flightline.flight_line_stop_time) ?? "",
                duration: StringUtils.stringOrEmpty(flightline.flight_line_duration),
                flown_agl: StringUtils.stringOrEmpty(flightline.flown_agl),
                notes: StringUtils.stringOrEmpty(flightline.notes),
                sensor_details: flightline.sensor_details ?? undefined,
                fe_planned_line_ids: flightline.fe_planned_line_ids,
            }

            const start = TimeUtils.parseDate(flightline.flight_line_start_time)
            const stop = TimeUtils.parseDate(flightline.flight_line_stop_time)
            if (stop?.isAfter(start)) {
                crossesMidnight = true
            }
        }

        set({
            ...initialEventEditorState,
            values: { ...initialCollectionEventCreation, ...lineInfo },
            crossesMidnight,
            isDrawerOpen: true,
        })
    },
    updateValues: (values) => set({ values }),
    setErrors: (errors) => set({ errors }),
    setCrossesMidnight: (bool) => set({ crossesMidnight: bool }),
    formIsValid: () => {
        const errors = get().errors
        return Object.values(errors).every((x) => x === "")
    },
}))
